import { useState } from 'react';
import type { VenueType, PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';

interface ChipDef {
  label: string;
  type: 'venueType' | 'atmosphere' | 'amenity';
  value: string;
}

// Primary chips — always visible. These answer the top-level
// "what kind of place am I looking for?" decision.
const PRIMARY_CHIPS: ChipDef[] = [
  { label: 'Wineries', type: 'venueType', value: 'winery' },
  { label: 'Tasting Rooms', type: 'venueType', value: 'tasting room' },
  { label: 'Wine Bars', type: 'venueType', value: 'wine bar' },
];

// Secondary chips — atmosphere + amenity refinements, hidden behind
// "More filters" until the user opts in. Keeps the default view
// focused on the top-level venue-type decision.
const SECONDARY_CHIPS: ChipDef[] = [
  { label: 'Rustic', type: 'atmosphere', value: 'rustic' },
  { label: 'Upscale', type: 'atmosphere', value: 'upscale' },
  { label: 'Casual', type: 'atmosphere', value: 'casual' },
  { label: 'Pet Friendly', type: 'amenity', value: 'pet-friendly' },
  { label: 'Scenic Views', type: 'amenity', value: 'scenic-views' },
  { label: 'Outdoor Seating', type: 'amenity', value: 'outdoor-seating' },
  { label: 'Food Service', type: 'amenity', value: 'food-service' },
  { label: 'Live Music', type: 'amenity', value: 'live-music' },
  { label: 'Family Friendly', type: 'amenity', value: 'family-friendly' },
];

interface CategoryChipsProps {
  selectedVenueTypes: VenueType[];
  selectedAtmospheres: PrimaryAtmosphere[];
  selectedAmenities: Amenity[];
  onToggleVenueType: (v: VenueType) => void;
  onToggleAtmosphere: (a: PrimaryAtmosphere) => void;
  onToggleAmenity: (a: Amenity) => void;
}

export const CategoryChips = ({
  selectedVenueTypes,
  selectedAtmospheres,
  selectedAmenities,
  onToggleVenueType,
  onToggleAtmosphere,
  onToggleAmenity,
}: CategoryChipsProps) => {
  const isActive = (chip: ChipDef) => {
    if (chip.type === 'venueType') return selectedVenueTypes.includes(chip.value as VenueType);
    if (chip.type === 'atmosphere') return selectedAtmospheres.includes(chip.value as PrimaryAtmosphere);
    return selectedAmenities.includes(chip.value as Amenity);
  };

  const handleToggle = (chip: ChipDef) => {
    if (chip.type === 'venueType') onToggleVenueType(chip.value as VenueType);
    else if (chip.type === 'atmosphere') onToggleAtmosphere(chip.value as PrimaryAtmosphere);
    else onToggleAmenity(chip.value as Amenity);
  };

  // Count how many secondary filters are currently active, so the
  // "More filters" button can surface that state to the user.
  const activeSecondaryCount = SECONDARY_CHIPS.filter(isActive).length;

  // Auto-expand the drawer when any secondary filter is active on
  // first render so active state is never hidden behind a closed drawer.
  const [isExpanded, setIsExpanded] = useState(activeSecondaryCount > 0);

  const renderChip = (chip: ChipDef) => {
    const active = isActive(chip);
    return (
      <button
        key={`${chip.type}-${chip.value}`}
        type="button"
        onClick={() => handleToggle(chip)}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          active
            ? 'bg-primary-700 text-white hover:bg-primary-800'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {chip.label}
      </button>
    );
  };

  return (
    <div className="space-y-3">
      {/* Primary row: top-level venue-type decision + More-filters toggle */}
      <div className="flex flex-wrap gap-2 items-center">
        {PRIMARY_CHIPS.map(renderChip)}
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-controls="more-filters-drawer"
          className="px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors inline-flex items-center gap-1.5"
        >
          <span>More filters</span>
          {activeSecondaryCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary-700 text-white text-xs font-semibold">
              {activeSecondaryCount}
            </span>
          )}
          <svg
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Secondary drawer: atmosphere + amenity refinements. */}
      {isExpanded && (
        <div id="more-filters-drawer" className="flex flex-wrap gap-2 pt-1">
          {SECONDARY_CHIPS.map(renderChip)}
        </div>
      )}
    </div>
  );
};
